import {
  CHANGE_TYPES,
  JURISDICTIONS,
  LEGISLATION_KINDS,
  type ChangeType,
  type Jurisdiction,
  type LegislationCategory,
  type LegislationKind,
  type LegislationStatus,
  type NodeType,
} from "./enums";
import { parseOneOf } from "./guards";
import { textDoc, type RichTextDoc } from "./richText";
import { err, ok, type Result } from "./result";
import { formatAuDate } from "./time";
import { formatCountLabel, normalizeQueryText, promptedOptionalText, promptedRequiredText } from "./text";

/**
 * Pure helpers for legislation structure: building the section tree from a flat
 * list, filtering for client visibility, and formatting titles.
 */

export type LegislationCreateDraft = {
  readonly title: string;
  readonly reference: string;
  readonly jurisdiction: Jurisdiction;
  readonly kind: LegislationKind;
  readonly category?: LegislationCategory;
};

export const emptyLegislationCreateFormState = (): LegislationCreateDraft => ({
  title: "",
  reference: "",
  jurisdiction: "COM",
  kind: "act",
});

export type PreparedLegislationCreate = {
  readonly title: string;
  readonly reference: string;
  readonly jurisdiction: Jurisdiction;
  readonly kind: LegislationKind;
  readonly category: LegislationCategory;
};

export type LegislationInsertInput = PreparedLegislationCreate & {
  readonly inMarkup?: boolean;
};

export type PreparedLegislationInsert = PreparedLegislationCreate & {
  readonly inMarkup: boolean;
  readonly status: "active";
  readonly repealInfo: null;
};

export type ImportedLegislationInput = PreparedLegislationCreate & {
  readonly status: LegislationStatus;
};

export type ImportedLegislationPatch = {
  readonly title: string;
  readonly kind: LegislationKind;
  readonly status: LegislationStatus;
};

export type LegislationListFilter =
  | { readonly kind: "all" }
  | {
      readonly kind: "byJurisdiction";
      readonly jurisdiction: Jurisdiction;
      readonly status: LegislationStatus | null;
    }
  | {
      readonly kind: "byStatus";
      readonly status: LegislationStatus;
    };

export type LegislationListFilterInput = {
  readonly jurisdiction?: Jurisdiction;
  readonly status?: LegislationStatus;
};

export type LegislationRepealInput<LegislationId extends string = string> = {
  readonly replacedByTitle: string;
  readonly replacedByLegislationId: LegislationId | null;
  readonly effectiveDate: number;
};

export type LegislationRepealPatch<LegislationId extends string = string> = {
  readonly status: "inactive";
  readonly repealInfo: LegislationRepealInput<LegislationId>;
};

export type LegislationCreateError =
  | { readonly kind: "missingTitle" }
  | { readonly kind: "missingReference" }
  | { readonly kind: "invalidReference"; readonly reference: string };

export const legislationCreateErrorMessage = (error: LegislationCreateError): string => {
  switch (error.kind) {
    case "missingTitle":
      return "Title is required.";
    case "missingReference":
      return "Reference is required.";
    case "invalidReference":
      return `Invalid legislation reference "${error.reference}" - expected uppercase alphanumeric.`;
  }
};

export const jurisdictionInputResult = (value: string): Result<Jurisdiction, { readonly kind: "notOneOf"; readonly value: string }> =>
  parseOneOf(JURISDICTIONS, value);

export const legislationListFilter = (
  input: LegislationListFilterInput,
): LegislationListFilter => {
  if (input.jurisdiction !== undefined) {
    return {
      kind: "byJurisdiction",
      jurisdiction: input.jurisdiction,
      status: input.status ?? null,
    };
  }
  if (input.status !== undefined) return { kind: "byStatus", status: input.status };
  return { kind: "all" };
};

export const jurisdictionFromInput = (value: string, fallback: Jurisdiction): Jurisdiction => {
  const parsed = jurisdictionInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const legislationKindInputResult = (value: string): Result<LegislationKind, { readonly kind: "notOneOf"; readonly value: string }> =>
  parseOneOf(LEGISLATION_KINDS, value);

export const legislationKindFromInput = (
  value: string,
  fallback: LegislationKind,
): LegislationKind => {
  const parsed = legislationKindInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type LegislationCreateFormAction =
  | { readonly type: "titleInput"; readonly value: string }
  | { readonly type: "referenceInput"; readonly value: string }
  | { readonly type: "jurisdictionInput"; readonly value: string }
  | { readonly type: "kindInput"; readonly value: string };

export const nextLegislationCreateFormState = (
  current: LegislationCreateDraft,
  action: LegislationCreateFormAction,
): LegislationCreateDraft => {
  switch (action.type) {
    case "titleInput":
      return { ...current, title: action.value };
    case "referenceInput":
      return { ...current, reference: normalizeLegislationReference(action.value) };
    case "jurisdictionInput":
      return { ...current, jurisdiction: jurisdictionFromInput(action.value, current.jurisdiction) };
    case "kindInput":
      return { ...current, kind: legislationKindFromInput(action.value, current.kind) };
  }
};

export const CHANGE_TYPE_OPTIONS = CHANGE_TYPES;

export const changeTypeDisplayName = (changeType: ChangeType): string => {
  const labels: Record<ChangeType, string> = {
    amended: "Amended",
    insertion: "Insertion",
    repeal: "Repeal",
  };
  return labels[changeType];
};

export type LegislationUpdateBadgeKind = "amended" | "repealed";

export const changeTypeStatusBadgeKind = (
  changeType: ChangeType,
): LegislationUpdateBadgeKind =>
  changeType === "repeal" ? "repealed" : "amended";

export const changeTypeInputResult = (value: string): Result<ChangeType, { readonly kind: "notOneOf"; readonly value: string }> =>
  parseOneOf(CHANGE_TYPE_OPTIONS, value);

export const changeTypeFromInput = (
  value: string,
  fallback: ChangeType,
): ChangeType => {
  const parsed = changeTypeInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type LegislationEditorButtonView = {
  readonly disabled: boolean;
  readonly label: string;
};

export type LegislationEditorPromptCommand<Command, Error> =
  | { readonly kind: "cancelled" }
  | { readonly kind: "invalid"; readonly error: Error }
  | { readonly kind: "ready"; readonly command: Command };

export type LegislationEditorPartCreateCommand<LegislationId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly parentId: null;
  readonly nodeType: "part";
  readonly number: "";
  readonly title: string;
};

export type LegislationEditorSectionCreateCommand<
  LegislationId extends string = string,
  SectionId extends string = string,
> = {
  readonly legislationId: LegislationId;
  readonly parentId: SectionId;
  readonly nodeType: "section";
  readonly number: string;
  readonly title: string;
};

export type LegislationEditorSectionRenameCommand<SectionId extends string = string> = {
  readonly id: SectionId;
  readonly number: string;
  readonly title: string;
};

export type LegislationEditorDefineTermCommand<LegislationId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly term: string;
  readonly definition: RichTextDoc;
};

export type LegislationEditorAmendmentDraftCommand = {
  readonly currentText: string;
  readonly instruction: string;
};

export type LegislationEditorDeleteCommand<LegislationId extends string = string> = {
  readonly id: LegislationId;
};

export type LegislationEditorPartCreateError = { readonly kind: "missingPartTitle" };
export type LegislationEditorSectionCreateError = { readonly kind: "missingSectionTitle" };
export type LegislationEditorDefineTermError = { readonly kind: "missingDefinition" };
export type LegislationEditorAmendmentDraftError = { readonly kind: "missingAmendmentInstruction" };

export type LegislationEditorCommandError =
  | LegislationEditorPartCreateError
  | LegislationEditorSectionCreateError
  | LegislationEditorDefineTermError
  | LegislationEditorAmendmentDraftError;

export const legislationEditorCommandErrorMessage = (error: LegislationEditorCommandError): string => {
  switch (error.kind) {
    case "missingPartTitle":
      return "Part title is required.";
    case "missingSectionTitle":
      return "Section title is required.";
    case "missingDefinition":
      return "Definition is required.";
    case "missingAmendmentInstruction":
      return "Amendment instruction is required.";
  }
};

export type LegislationEditorFeedbackTone = "error" | "success";

export type LegislationEditorFeedbackState =
  | { readonly kind: "hidden" }
  | { readonly kind: "visible"; readonly tone: LegislationEditorFeedbackTone; readonly message: string };

export type LegislationEditorFeedbackAction =
  | { readonly type: "cleared" }
  | { readonly type: "invalidCommand"; readonly error: LegislationEditorCommandError }
  | { readonly type: "operationFailed"; readonly message: string }
  | { readonly type: "operationSucceeded"; readonly message: string };

export const emptyLegislationEditorFeedbackState = (): LegislationEditorFeedbackState => ({ kind: "hidden" });

export const nextLegislationEditorFeedbackState = (
  _current: LegislationEditorFeedbackState,
  action: LegislationEditorFeedbackAction,
): LegislationEditorFeedbackState => {
  switch (action.type) {
    case "cleared":
      return { kind: "hidden" };
    case "invalidCommand":
      return { kind: "visible", tone: "error", message: legislationEditorCommandErrorMessage(action.error) };
    case "operationFailed":
      return { kind: "visible", tone: "error", message: action.message };
    case "operationSucceeded":
      return { kind: "visible", tone: "success", message: action.message };
  }
};

export const legislationEditorFeedbackClassName = (state: LegislationEditorFeedbackState): string => {
  if (state.kind === "hidden") return "";
  const base = "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm";
  return state.tone === "error"
    ? base + " border-red-200 bg-red-50 text-red-700"
    : base + " border-emerald-200 bg-emerald-50 text-emerald-700";
};

export type LegislationEditorDialogState<SectionId extends string = string> =
  | { readonly kind: "closed" }
  | { readonly kind: "addPart"; readonly title: string }
  | { readonly kind: "addSection"; readonly parentId: SectionId; readonly number: string; readonly title: string }
  | { readonly kind: "renameSection"; readonly sectionId: SectionId; readonly number: string; readonly title: string }
  | { readonly kind: "defineTerm"; readonly term: string; readonly definition: string }
  | { readonly kind: "draftAmendment"; readonly currentText: string; readonly instruction: string }
  | { readonly kind: "deleteLegislation"; readonly title: string };

export type LegislationEditorDialogAction<SectionId extends string = string> =
  | { readonly type: "openAddPart" }
  | { readonly type: "openAddSection"; readonly parentId: SectionId }
  | { readonly type: "openRenameSection"; readonly sectionId: SectionId; readonly number: string; readonly title: string }
  | { readonly type: "openDefineTerm"; readonly term: string }
  | { readonly type: "openDraftAmendment"; readonly currentText: string }
  | { readonly type: "openDeleteLegislation"; readonly title: string }
  | { readonly type: "numberInput"; readonly value: string }
  | { readonly type: "titleInput"; readonly value: string }
  | { readonly type: "definitionInput"; readonly value: string }
  | { readonly type: "instructionInput"; readonly value: string }
  | { readonly type: "closed" };

export const emptyLegislationEditorDialogState = <SectionId extends string = string>(): LegislationEditorDialogState<SectionId> => ({ kind: "closed" });

export const nextLegislationEditorDialogState = <SectionId extends string>(
  current: LegislationEditorDialogState<SectionId>,
  action: LegislationEditorDialogAction<SectionId>,
): LegislationEditorDialogState<SectionId> => {
  switch (action.type) {
    case "openAddPart":
      return { kind: "addPart", title: "" };
    case "openAddSection":
      return { kind: "addSection", parentId: action.parentId, number: "", title: "" };
    case "openRenameSection":
      return { kind: "renameSection", sectionId: action.sectionId, number: action.number, title: action.title };
    case "openDefineTerm":
      return { kind: "defineTerm", term: action.term, definition: "" };
    case "openDraftAmendment":
      return { kind: "draftAmendment", currentText: action.currentText, instruction: "" };
    case "openDeleteLegislation":
      return { kind: "deleteLegislation", title: action.title };
    case "numberInput":
      if (current.kind === "addSection") return { ...current, number: action.value };
      if (current.kind === "renameSection") return { ...current, number: action.value };
      return current;
    case "titleInput":
      if (current.kind === "addPart") return { ...current, title: action.value };
      if (current.kind === "addSection") return { ...current, title: action.value };
      if (current.kind === "renameSection") return { ...current, title: action.value };
      return current;
    case "definitionInput":
      return current.kind === "defineTerm" ? { ...current, definition: action.value } : current;
    case "instructionInput":
      return current.kind === "draftAmendment" ? { ...current, instruction: action.value } : current;
    case "closed":
      return emptyLegislationEditorDialogState<SectionId>();
  }
};

const LEGISLATION_EDITOR_DIALOG_TITLES = {
  closed: "",
  addPart: "Add part",
  addSection: "Add section",
  renameSection: "Rename section",
  defineTerm: "Define term",
  draftAmendment: "AI draft amendment",
  deleteLegislation: "Delete legislation",
} satisfies Record<LegislationEditorDialogState["kind"], string>;

const LEGISLATION_EDITOR_DIALOG_SUBMIT_LABELS = {
  closed: "",
  addPart: "Add part",
  addSection: "Add section",
  renameSection: "Rename",
  defineTerm: "Save definition",
  draftAmendment: "Draft amendment",
  deleteLegislation: "Delete",
} satisfies Record<LegislationEditorDialogState["kind"], string>;

export const legislationEditorDialogTitle = (state: LegislationEditorDialogState): string =>
  LEGISLATION_EDITOR_DIALOG_TITLES[state.kind];

export const legislationEditorDialogSubmitLabel = (state: LegislationEditorDialogState): string =>
  LEGISLATION_EDITOR_DIALOG_SUBMIT_LABELS[state.kind];

export const legislationEditorPartCreateCommand = <LegislationId extends string>({
  legislationId,
  titlePrompt,
}: {
  readonly legislationId: LegislationId;
  readonly titlePrompt: string | null;
}): LegislationEditorPromptCommand<
  LegislationEditorPartCreateCommand<LegislationId>,
  LegislationEditorPartCreateError
> => {
  const title = promptedRequiredText(titlePrompt, { kind: "missingPartTitle" } as const);
  if (title.kind === "cancelled") return { kind: "cancelled" };
  if (title.kind === "invalid") return { kind: "invalid", error: title.error };

  return {
    kind: "ready",
    command: {
      legislationId,
      parentId: null,
      nodeType: "part",
      number: "",
      title: title.value,
    },
  };
};

export const legislationEditorSectionCreateCommand = <LegislationId extends string, SectionId extends string>({
  legislationId,
  parentId,
  numberPrompt,
  titlePrompt,
}: {
  readonly legislationId: LegislationId;
  readonly parentId: SectionId;
  readonly numberPrompt: string | null;
  readonly titlePrompt: string | null;
}): LegislationEditorPromptCommand<
  LegislationEditorSectionCreateCommand<LegislationId, SectionId>,
  LegislationEditorSectionCreateError
> => {
  const number = promptedOptionalText(numberPrompt);
  if (number.kind === "cancelled") return { kind: "cancelled" };

  const title = promptedRequiredText(titlePrompt, { kind: "missingSectionTitle" } as const);
  if (title.kind === "cancelled") return { kind: "cancelled" };
  if (title.kind === "invalid") return { kind: "invalid", error: title.error };

  return {
    kind: "ready",
    command: {
      legislationId,
      parentId,
      nodeType: "section",
      number: number.value,
      title: title.value,
    },
  };
};

export const legislationEditorSectionRenameCommand = <SectionId extends string>({
  id,
  currentNumber,
  numberPrompt,
  titlePrompt,
}: {
  readonly id: SectionId;
  readonly currentNumber: string;
  readonly numberPrompt: string | null;
  readonly titlePrompt: string | null;
}): LegislationEditorPromptCommand<LegislationEditorSectionRenameCommand<SectionId>, never> => {
  const number = promptedOptionalText(numberPrompt);
  const title = promptedOptionalText(titlePrompt);
  if (title.kind === "cancelled") return { kind: "cancelled" };

  return {
    kind: "ready",
    command: {
      id,
      number: number.kind === "ready" ? number.value : currentNumber,
      title: title.value,
    },
  };
};

export const legislationEditorDefineTermCommand = <LegislationId extends string>({
  legislationId,
  term,
  definitionPrompt,
}: {
  readonly legislationId: LegislationId;
  readonly term: string;
  readonly definitionPrompt: string | null;
}): LegislationEditorPromptCommand<
  LegislationEditorDefineTermCommand<LegislationId>,
  LegislationEditorDefineTermError
> => {
  const definition = promptedRequiredText(definitionPrompt, { kind: "missingDefinition" } as const);
  if (definition.kind === "cancelled") return { kind: "cancelled" };
  if (definition.kind === "invalid") return { kind: "invalid", error: definition.error };

  return {
    kind: "ready",
    command: {
      legislationId,
      term,
      definition: textDoc(definition.value),
    },
  };
};

export const legislationEditorAmendmentDraftCommand = ({
  currentText,
  instructionPrompt,
}: {
  readonly currentText: string;
  readonly instructionPrompt: string | null;
}): LegislationEditorPromptCommand<
  LegislationEditorAmendmentDraftCommand,
  LegislationEditorAmendmentDraftError
> => {
  const instruction = promptedRequiredText(instructionPrompt, { kind: "missingAmendmentInstruction" } as const);
  if (instruction.kind === "cancelled") return { kind: "cancelled" };
  if (instruction.kind === "invalid") return { kind: "invalid", error: instruction.error };

  return { kind: "ready", command: { currentText, instruction: instruction.value } };
};

export const legislationEditorDeleteCommand = <LegislationId extends string>({
  id,
  confirmed,
}: {
  readonly id: LegislationId;
  readonly confirmed: boolean;
}): LegislationEditorPromptCommand<LegislationEditorDeleteCommand<LegislationId>, never> =>
  confirmed ? { kind: "ready", command: { id } } : { kind: "cancelled" };

export type LegislationEditorDialogPreparedCommand<
  LegislationId extends string = string,
  SectionId extends string = string,
> =
  | { readonly kind: "none" }
  | { readonly kind: "invalid"; readonly error: LegislationEditorCommandError }
  | { readonly kind: "createPart"; readonly command: LegislationEditorPartCreateCommand<LegislationId> }
  | { readonly kind: "createSection"; readonly command: LegislationEditorSectionCreateCommand<LegislationId, SectionId> }
  | { readonly kind: "renameSection"; readonly command: LegislationEditorSectionRenameCommand<SectionId> }
  | { readonly kind: "defineTerm"; readonly command: LegislationEditorDefineTermCommand<LegislationId> }
  | { readonly kind: "draftAmendment"; readonly command: LegislationEditorAmendmentDraftCommand }
  | { readonly kind: "deleteLegislation"; readonly command: LegislationEditorDeleteCommand<LegislationId> };

export const legislationEditorDialogPreparedCommand = <LegislationId extends string, SectionId extends string>({
  state,
  legislationId,
}: {
  readonly state: LegislationEditorDialogState<SectionId>;
  readonly legislationId: LegislationId;
}): LegislationEditorDialogPreparedCommand<LegislationId, SectionId> => {
  switch (state.kind) {
    case "closed":
      return { kind: "none" };
    case "addPart": {
      const prepared = legislationEditorPartCreateCommand({ legislationId, titlePrompt: state.title });
      if (prepared.kind === "invalid") return { kind: "invalid", error: prepared.error };
      return prepared.kind === "ready" ? { kind: "createPart", command: prepared.command } : { kind: "none" };
    }
    case "addSection": {
      const prepared = legislationEditorSectionCreateCommand({
        legislationId,
        parentId: state.parentId,
        numberPrompt: state.number,
        titlePrompt: state.title,
      });
      if (prepared.kind === "invalid") return { kind: "invalid", error: prepared.error };
      return prepared.kind === "ready" ? { kind: "createSection", command: prepared.command } : { kind: "none" };
    }
    case "renameSection": {
      const prepared = legislationEditorSectionRenameCommand({
        id: state.sectionId,
        currentNumber: state.number,
        numberPrompt: state.number,
        titlePrompt: state.title,
      });
      return prepared.kind === "ready" ? { kind: "renameSection", command: prepared.command } : { kind: "none" };
    }
    case "defineTerm": {
      const prepared = legislationEditorDefineTermCommand({
        legislationId,
        term: state.term,
        definitionPrompt: state.definition,
      });
      if (prepared.kind === "invalid") return { kind: "invalid", error: prepared.error };
      return prepared.kind === "ready" ? { kind: "defineTerm", command: prepared.command } : { kind: "none" };
    }
    case "draftAmendment": {
      const prepared = legislationEditorAmendmentDraftCommand({
        currentText: state.currentText,
        instructionPrompt: state.instruction,
      });
      if (prepared.kind === "invalid") return { kind: "invalid", error: prepared.error };
      return prepared.kind === "ready" ? { kind: "draftAmendment", command: prepared.command } : { kind: "none" };
    }
    case "deleteLegislation": {
      const prepared = legislationEditorDeleteCommand({ id: legislationId, confirmed: true });
      return prepared.kind === "ready" ? { kind: "deleteLegislation", command: prepared.command } : { kind: "none" };
    }
  }
};

export type LegislationStructureExtractState<Node = unknown> = {
  readonly open: boolean;
  readonly sourceText: string;
  readonly nodes: Node[] | null;
  readonly busy: boolean;
  readonly error: string | null;
};

export const emptyLegislationStructureExtractState = <Node = unknown>(): LegislationStructureExtractState<Node> => ({
  open: false,
  sourceText: "",
  nodes: null,
  busy: false,
  error: null,
});

export type LegislationStructureExtractAction<Node = unknown> =
  | { readonly type: "toggleOpen" }
  | { readonly type: "sourceTextInput"; readonly value: string }
  | { readonly type: "extractStarted" }
  | { readonly type: "extractSucceeded"; readonly nodes: readonly Node[] }
  | { readonly type: "extractFailed"; readonly error: string }
  | { readonly type: "inserted" };

export const nextLegislationStructureExtractState = <Node>(
  current: LegislationStructureExtractState<Node>,
  action: LegislationStructureExtractAction<Node>,
): LegislationStructureExtractState<Node> => {
  switch (action.type) {
    case "toggleOpen":
      return { ...current, open: !current.open };
    case "sourceTextInput":
      return { ...current, sourceText: action.value, nodes: null, error: null };
    case "extractStarted":
      return { ...current, busy: true, error: null };
    case "extractSucceeded":
      return { ...current, nodes: [...action.nodes], busy: false, error: null };
    case "extractFailed":
      return { ...current, busy: false, error: action.error };
    case "inserted":
      return emptyLegislationStructureExtractState<Node>();
  }
};

export type StructureExtractButtonInput = {
  readonly sourceText: string;
  readonly busy: boolean;
};

export const structureExtractButtonView = ({
  sourceText,
  busy,
}: StructureExtractButtonInput): LegislationEditorButtonView => ({
  disabled: busy || sourceText.trim() === "",
  label: busy ? "Extracting…" : "Extract with AI",
});

export const insertStructureNodesButtonLabel = (count: number): string =>
  "Insert " + formatCountLabel(count, "node");

export type StructurePreviewNodeInput = {
  readonly nodeType: NodeType;
  readonly number: string;
  readonly title: string;
};

export type StructurePreviewNodeView = {
  readonly paddingLeft: number;
  readonly className: string;
  readonly label: string;
};

export const structurePreviewNodeView = (
  node: StructurePreviewNodeInput,
): StructurePreviewNodeView => ({
  paddingLeft: node.nodeType === "section" ? 20 : 0,
  className: node.nodeType === "section" ? "text-slate-600" : "font-semibold text-slate-800",
  label: [node.number, node.title].filter((part) => part.trim() !== "").join(" "),
});

export type SectionTreeRowViewInput<SectionId extends string = string> = {
  readonly node: Pick<SectionTreeNode<SectionId>, "id" | "nodeType" | "number" | "title" | "hidden">;
  readonly selectedId: SectionId | null;
  readonly depth: number;
};

export type SectionTreeRowView = {
  readonly isContainer: boolean;
  readonly paddingLeft: number;
  readonly rowClassName: string;
  readonly buttonClassName: string;
  readonly label: string;
  readonly visibilityToggleTitle: string;
  readonly visibilityToggleIcon: string;
};

export const sectionTreeRowView = <SectionId extends string>({
  node,
  selectedId,
  depth,
}: SectionTreeRowViewInput<SectionId>): SectionTreeRowView => {
  const isContainer = node.nodeType !== "section";
  return {
    isContainer,
    paddingLeft: depth * 10 + 4,
    rowClassName: "group flex items-center gap-1 rounded px-1 py-1 " +
      (node.id === selectedId ? "bg-brand-light" : "hover:bg-slate-100"),
    buttonClassName: "flex-1 truncate text-left " +
      (node.hidden ? "italic text-slate-400 line-through" : isContainer ? "font-semibold text-slate-700" : ""),
    label: isContainer ? node.title : `${node.number}. ${node.title}`,
    visibilityToggleTitle: node.hidden ? "Restore" : "Repeal (hide)",
    visibilityToggleIcon: node.hidden ? "⤺" : "⦸",
  };
};

export const legislationDeleteConfirmationMessage = (title: string): string =>
  `Delete "${title}" and all its content? This cannot be undone.`;

export const isLegislationCreateDraftTouched = (draft: LegislationCreateDraft): boolean =>
  draft.title.trim() !== "" || draft.reference.trim() !== "";

export const normalizeLegislationReference = (reference: string): string =>
  reference.trim().toUpperCase();

export type LegislationReferenceInputState = "empty" | "valid" | "invalid";

export const legislationReferenceInputState = (
  reference: string,
): LegislationReferenceInputState => {
  const normalized = normalizeLegislationReference(reference);
  if (normalized === "") return "empty";
  return isValidReference(normalized) ? "valid" : "invalid";
};

export const prepareLegislationCreate = (
  draft: LegislationCreateDraft,
): Result<PreparedLegislationCreate, LegislationCreateError> => {
  const title = draft.title.trim();
  if (title === "") return err({ kind: "missingTitle" });

  const reference = normalizeLegislationReference(draft.reference);
  if (reference === "") return err({ kind: "missingReference" });
  if (!isValidReference(reference)) {
    return err({ kind: "invalidReference", reference });
  }

  return ok({
    title,
    reference,
    jurisdiction: draft.jurisdiction,
    kind: draft.kind,
    category: draft.category ?? "legislative",
  });
};

const LEGISLATION_REFERENCE_INPUT_CLASS_NAMES = {
  empty: "border-slate-300",
  valid: "border-slate-300",
  invalid: "border-red-400",
} satisfies Record<LegislationReferenceInputState, string>;

export type LegislationCreateFormView = {
  readonly prepared: Result<PreparedLegislationCreate, LegislationCreateError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly referenceInputState: LegislationReferenceInputState;
  readonly referenceInputClassName: string;
  readonly canSubmit: boolean;
};

export const legislationReferenceInputClassName = (
  state: LegislationReferenceInputState,
): string => LEGISLATION_REFERENCE_INPUT_CLASS_NAMES[state];

export const legislationCreateFormView = (
  draft: LegislationCreateDraft,
): LegislationCreateFormView => {
  const prepared = prepareLegislationCreate(draft);
  const touched = isLegislationCreateDraftTouched(draft);
  const referenceInputState = legislationReferenceInputState(draft.reference);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? legislationCreateErrorMessage(prepared.error) : null,
    referenceInputState,
    referenceInputClassName: legislationReferenceInputClassName(referenceInputState),
    canSubmit: prepared.ok,
  };
};

export type LegislationCreateSubmissionState =
  | { readonly kind: "idle"; readonly error: null }
  | { readonly kind: "creating"; readonly error: null }
  | { readonly kind: "failed"; readonly error: string };

export const emptyLegislationCreateSubmissionState = (): LegislationCreateSubmissionState => ({
  kind: "idle",
  error: null,
});

export type LegislationCreateSubmissionAction =
  | { readonly type: "cleared" }
  | { readonly type: "started" }
  | { readonly type: "failed"; readonly error: string };

export const nextLegislationCreateSubmissionState = (
  _current: LegislationCreateSubmissionState,
  action: LegislationCreateSubmissionAction,
): LegislationCreateSubmissionState => {
  switch (action.type) {
    case "cleared":
      return emptyLegislationCreateSubmissionState();
    case "started":
      return { kind: "creating", error: null };
    case "failed":
      return { kind: "failed", error: action.error };
  }
};

export const legislationCreateSubmitButtonLabel = (
  state: LegislationCreateSubmissionState,
): "Create & edit" | "Creating..." => state.kind === "creating" ? "Creating..." : "Create & edit";

export const legislationCreateSubmissionError = (state: LegislationCreateSubmissionState): string | null =>
  state.kind === "failed" ? state.error : null;

export const prepareLegislationInsert = (
  input: LegislationInsertInput,
): PreparedLegislationInsert => ({
  title: input.title,
  reference: input.reference,
  jurisdiction: input.jurisdiction,
  kind: input.kind,
  category: input.category,
  inMarkup: input.inMarkup ?? true,
  status: "active",
  repealInfo: null,
});

export const prepareImportedLegislationInsert = (
  input: ImportedLegislationInput,
): ImportedLegislationInput & { readonly inMarkup: true; readonly repealInfo: null } => ({
  ...input,
  inMarkup: true,
  repealInfo: null,
});

export const importedLegislationPatch = (input: ImportedLegislationInput): ImportedLegislationPatch => ({
  title: input.title,
  kind: input.kind,
  status: input.status,
});

export const legislationRepealPatch = <LegislationId extends string>(
  input: LegislationRepealInput<LegislationId>,
): LegislationRepealPatch<LegislationId> => ({
  status: "inactive",
  repealInfo: {
    replacedByTitle: input.replacedByTitle,
    replacedByLegislationId: input.replacedByLegislationId,
    effectiveDate: input.effectiveDate,
  },
});

export type FlatSection<SectionId extends string = string> = {
  readonly id: SectionId;
  readonly parentId: SectionId | null;
  readonly nodeType: NodeType;
  readonly number: string;
  readonly title: string;
  readonly order: number;
  readonly hidden: boolean;
};

export type SectionTreeNode<SectionId extends string = string> = FlatSection<SectionId> & {
  readonly children: readonly SectionTreeNode<SectionId>[];
};

export type SectionOrderRow<SectionId extends string = string> = {
  readonly _id: SectionId;
  readonly parentId: SectionId | null;
  readonly order: number;
};

export type SectionMoveDirection = -1 | 1;

export type SectionSiblingMoveResult<SectionId extends string = string> =
  | { readonly kind: "changed"; readonly orderedIds: SectionId[] }
  | { readonly kind: "unchanged"; readonly reason: "sectionNotFound" | "atBoundary" };

export type SectionSiblingMoveInput<Row extends SectionOrderRow> = {
  readonly sections: readonly Row[];
  readonly sectionId: Row["_id"];
  readonly direction: SectionMoveDirection;
};

export type SectionSearchTextDraft = {
  readonly number: string;
  readonly title: string;
  readonly contentText: string;
};

export type SectionInsertDraft<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly parentId: SectionId | null;
  readonly nodeType: NodeType;
  readonly number: string;
  readonly title: string;
  readonly content: RichTextDoc;
  readonly contentText: string;
  readonly order: number;
};

export type PreparedSectionInsert<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly parentId: SectionId | null;
  readonly nodeType: NodeType;
  readonly number: string;
  readonly title: string;
  readonly content: RichTextDoc;
  readonly searchText: string;
  readonly order: number;
  readonly hidden: false;
  readonly updateMarkup: null;
};

export type SectionContentPatch = {
  readonly content: RichTextDoc;
  readonly searchText: string;
};

export type SectionRenamePatch = {
  readonly number: string;
  readonly title: string;
  readonly searchText: string;
};

export type SectionVisibilityPatch = {
  readonly hidden: boolean;
};

export type SectionUpdateMarkupPatch = {
  readonly updateMarkup: RichTextDoc | null;
};

export type SectionSearchTextPatch = {
  readonly searchText: string;
};

export type SectionOrderPatch = {
  readonly order: number;
};

export const cleanSearchText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

export const sectionSearchText = (draft: SectionSearchTextDraft): string =>
  cleanSearchText([draft.number, draft.title, draft.contentText].filter((part) => part.trim() !== "").join(" "));

export const prepareSectionInsert = <LegislationId extends string, SectionId extends string>(
  draft: SectionInsertDraft<LegislationId, SectionId>,
): PreparedSectionInsert<LegislationId, SectionId> => ({
  legislationId: draft.legislationId,
  parentId: draft.parentId,
  nodeType: draft.nodeType,
  number: draft.number,
  title: draft.title,
  content: draft.content,
  searchText: sectionSearchText({
    number: draft.number,
    title: draft.title,
    contentText: draft.contentText,
  }),
  order: draft.order,
  hidden: false,
  updateMarkup: null,
});

export const prepareSectionContentPatch = (input: {
  readonly currentNumber: string;
  readonly currentTitle: string;
  readonly content: RichTextDoc;
  readonly contentText: string;
}): SectionContentPatch => ({
  content: input.content,
  searchText: sectionSearchText({
    number: input.currentNumber,
    title: input.currentTitle,
    contentText: input.contentText,
  }),
});

export const prepareSectionRenamePatch = (input: {
  readonly number: string;
  readonly title: string;
  readonly contentText: string;
}): SectionRenamePatch => ({
  number: input.number,
  title: input.title,
  searchText: sectionSearchText(input),
});

export const sectionVisibilityPatch = (hidden: boolean): SectionVisibilityPatch => ({
  hidden,
});

export const sectionUpdateMarkupPatch = (
  updateMarkup: RichTextDoc | null,
): SectionUpdateMarkupPatch => ({
  updateMarkup,
});

export const sectionSearchTextPatch = (searchText: string): SectionSearchTextPatch => ({
  searchText,
});

export const sectionOrderPatch = (order: number): SectionOrderPatch => ({
  order,
});

const SEARCH_TERM_RE = /[a-z0-9][a-z0-9-]{1,}/g;

const searchTermFromMatch = (match: RegExpMatchArray): string | null => {
  const [raw] = match;
  if (raw === undefined) return null;
  const term = raw.replace(/^-+|-+$/g, "");
  return term.length < 2 ? null : term;
};

export const searchTerms = (query: string): string[] => {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const match of query.toLowerCase().matchAll(SEARCH_TERM_RE)) {
    const term = searchTermFromMatch(match);
    if (term === null || seen.has(term)) continue;
    seen.add(term);
    terms.push(term);
  }
  return terms;
};

type SearchPassage = {
  readonly start: number;
  readonly end: number;
  readonly text: string;
};

export const sectionSearchSnippet = (text: string, query: string, maxLength = 220): string => {
  const limit = normalizedSnippetLength(maxLength);
  if (limit === 0) return "";

  const cleaned = cleanSearchText(text);
  if (cleaned.length <= limit) return cleaned;

  const phrase = normalizeQueryText(query);
  const terms = searchTerms(query);
  const passages = splitSearchPassages(cleaned);
  const scored = passages.map((passage, index) => ({
    passage,
    index,
    score: scoreSearchPassage(passage.text, phrase, terms),
  }));
  scored.sort((a, b) => b.score - a.score || a.index - b.index);

  const best = scored[0];
  if (best === undefined || best.score <= 0) return truncateSearchSnippet(cleaned, limit);

  return excerptAround(cleaned, best.passage.start, best.passage.end, limit);
};

const SEARCH_PASSAGE_RE = /[^.!?;:]+[.!?;:]?/g;

const searchPassageFromMatch = (match: RegExpMatchArray): SearchPassage | null => {
  const [raw] = match;
  if (raw === undefined) return null;

  const leading = raw.length - raw.trimStart().length;
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  const start = (match.index ?? 0) + leading;
  return { start, end: start + trimmed.length, text: trimmed };
};

const splitSearchPassages = (text: string): SearchPassage[] => {
  const passages: SearchPassage[] = [];
  for (const match of text.matchAll(SEARCH_PASSAGE_RE)) {
    const passage = searchPassageFromMatch(match);
    if (passage !== null) passages.push(passage);
  }
  return passages.length === 0 ? [{ start: 0, end: text.length, text }] : passages;
};

const scoreSearchPassage = (
  passage: string,
  phrase: string,
  terms: readonly string[],
): number => {
  const lower = passage.toLowerCase();
  let score = 0;
  if (phrase.length >= 2 && lower.includes(phrase)) score += 14 + Math.min(phrase.length, 24);
  for (const term of terms) {
    if (!lower.includes(term)) continue;
    score += term.length >= 6 ? 6 : 3;
  }
  return score;
};

const excerptAround = (
  text: string,
  passageStart: number,
  passageEnd: number,
  maxLength: number,
): string => {
  if (text.length <= maxLength) return text;

  const passageLength = passageEnd - passageStart;
  const center = Math.floor((passageStart + passageEnd) / 2);
  const preferredStart = passageLength >= maxLength
    ? passageStart
    : Math.max(0, center - Math.floor(maxLength / 2));
  let start = Math.max(0, Math.min(preferredStart, text.length - maxLength));
  let end = Math.min(text.length, start + maxLength);

  if (start > 0) {
    const wordStart = text.indexOf(" ", start);
    if (wordStart > 0 && wordStart < passageStart) start = wordStart + 1;
  }
  if (end < text.length) {
    const wordEnd = text.lastIndexOf(" ", end);
    if (wordEnd > passageEnd && wordEnd > start) end = wordEnd;
  }

  const excerpt = text.slice(start, end).trim();
  return (start > 0 ? "..." : "") + excerpt + (end < text.length ? "..." : "");
};

const normalizedSnippetLength = (maxLength: number): number => {
  if (!Number.isFinite(maxLength)) return 220;
  return Math.max(0, Math.floor(maxLength));
};

const truncateSearchSnippet = (text: string, maxLength: number): string => {
  if (maxLength === 0) return "";
  if (text.length <= maxLength) return text;
  if (maxLength <= 3) return ".".repeat(maxLength);
  return text.slice(0, maxLength - 3).trimEnd() + "...";
};

/**
 * Build the nested Part > Division > Subdivision > Section tree from a flat
 * list. Siblings are sorted by `order`. Orphans (unknown parent) are skipped.
 */
export const buildSectionTree = <SectionId extends string>(
  sections: readonly FlatSection<SectionId>[],
): SectionTreeNode<SectionId>[] => {
  const knownIds = new Set<SectionId>(sections.map((section) => section.id));
  const childrenByParent = new Map<SectionId | null, FlatSection<SectionId>[]>();

  for (const section of sections) {
    if (section.parentId !== null && !knownIds.has(section.parentId)) continue;
    const siblings = childrenByParent.get(section.parentId) ?? [];
    childrenByParent.set(section.parentId, [...siblings, section]);
  }

  const buildChildren = (parentId: SectionId | null): SectionTreeNode<SectionId>[] =>
    [...(childrenByParent.get(parentId) ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((section) => ({ ...section, children: buildChildren(section.id) }));

  return buildChildren(null);
};

export const firstVisibleSectionNode = <SectionId extends string>(
  nodes: readonly SectionTreeNode<SectionId>[],
): SectionTreeNode<SectionId> | null => {
  for (const node of nodes) {
    if (node.nodeType === "section" && !node.hidden) return node;
    const child = firstVisibleSectionNode(node.children);
    if (child !== null) return child;
  }
  return null;
};

export const firstSectionTreeLeaf = <SectionId extends string>(
  nodes: readonly SectionTreeNode<SectionId>[],
): SectionTreeNode<SectionId> | null => {
  for (const node of nodes) {
    if (node.nodeType === "section") return node;
    const child = firstSectionTreeLeaf(node.children);
    if (child !== null) return child;
  }
  return null;
};

export type SectionAncestorRow<SectionId extends string = string> = {
  readonly _id: SectionId;
  readonly parentId: SectionId | null;
};

export type LegislationViewerSectionRow<SectionId extends string = string> = SectionAncestorRow<SectionId> & {
  readonly hidden: boolean;
};

export type LegislationViewerUpdateRow<SectionId extends string = string> = {
  readonly appliesToSectionIds: readonly SectionId[];
};

export type LegislationEditorSectionRow<SectionId extends string = string> = {
  readonly _id: SectionId;
};

export type LegislationEditorSelectedSectionInput<
  SectionId extends string,
  Section extends LegislationEditorSectionRow<SectionId>,
> = {
  readonly sections: readonly Section[];
  readonly selectedId: SectionId | null;
};

export type LegislationEditorNextSectionIdInput<SectionId extends string> = {
  readonly tree: readonly SectionTreeNode<SectionId>[];
  readonly selectedId: SectionId | null;
};

export type LegislationViewerTabId = "legislation" | "updates";

export type LegislationViewerTab = {
  readonly id: LegislationViewerTabId;
  readonly label: string;
  readonly count: number;
};

export type LegislationViewerSelectedSectionInput<
  SectionId extends string,
  Section extends LegislationViewerSectionRow<SectionId>,
> = {
  readonly sections: readonly Section[];
  readonly selectedId: SectionId | null;
};

export type LegislationViewerNextSectionIdInput<
  SectionId extends string,
  Section extends LegislationViewerSectionRow<SectionId>,
> = {
  readonly sections: readonly Section[];
  readonly tree: readonly SectionTreeNode<SectionId>[];
  readonly requestedSectionId: SectionId | null;
  readonly selectedId: SectionId | null;
};

export const sectionAncestors = <Row extends SectionAncestorRow>({
  section,
  sections,
}: {
  readonly section: Row;
  readonly sections: readonly Row[];
}): Row[] => {
  const byId = new Map(sections.map((row) => [row._id, row]));
  const chain: Row[] = [];
  let parentId = section.parentId;

  while (parentId !== null) {
    const parent = byId.get(parentId);
    if (parent === undefined) break;
    chain.unshift(parent);
    parentId = parent.parentId;
  }

  return chain;
};

export const legislationEditorSelectedSection = <
  SectionId extends string,
  Section extends LegislationEditorSectionRow<SectionId>,
>({
  sections,
  selectedId,
}: LegislationEditorSelectedSectionInput<SectionId, Section>): Section | null =>
  selectedId === null ? null : sections.find((section) => section._id === selectedId) ?? null;

export const nextLegislationEditorSectionId = <SectionId extends string>({
  tree,
  selectedId,
}: LegislationEditorNextSectionIdInput<SectionId>): SectionId | null =>
  selectedId !== null ? selectedId : firstVisibleSectionNode(tree)?.id ?? null;

export const legislationViewerSelectedSection = <
  SectionId extends string,
  Section extends LegislationViewerSectionRow<SectionId>,
>({
  sections,
  selectedId,
}: LegislationViewerSelectedSectionInput<SectionId, Section>): Section | null =>
  selectedId === null ? null : sections.find((section) => section._id === selectedId) ?? null;

export const legislationViewerSectionUpdates = <
  SectionId extends string,
  Update extends LegislationViewerUpdateRow<SectionId>,
>({
  selectedSectionId,
  updates,
}: {
  readonly selectedSectionId: SectionId | null;
  readonly updates: readonly Update[];
}): Update[] =>
  selectedSectionId === null
    ? []
    : updates.filter((update) => update.appliesToSectionIds.includes(selectedSectionId));

export const legislationViewerTabs = (updateCount: number): LegislationViewerTab[] => [
  { id: "legislation", label: "Legislation", count: 0 },
  { id: "updates", label: "Updates", count: updateCount },
];

export const nextLegislationViewerSectionId = <
  SectionId extends string,
  Section extends LegislationViewerSectionRow<SectionId>,
>({
  sections,
  tree,
  requestedSectionId,
  selectedId,
}: LegislationViewerNextSectionIdInput<SectionId, Section>): SectionId | null => {
  if (requestedSectionId !== null) {
    const requested = sections.find((section) => section._id === requestedSectionId && !section.hidden);
    if (requested !== undefined) return requested._id;
  }
  if (selectedId !== null) return selectedId;
  return firstSectionTreeLeaf(tree)?.id ?? null;
};

export const filterSectionTree = <SectionId extends string>(
  nodes: readonly SectionTreeNode<SectionId>[],
  query: string,
): SectionTreeNode<SectionId>[] => {
  const normalizedQuery = normalizeQueryText(query);
  if (normalizedQuery === "") return [...nodes];

  const walk = (list: readonly SectionTreeNode<SectionId>[]): SectionTreeNode<SectionId>[] => {
    const out: SectionTreeNode<SectionId>[] = [];
    for (const node of list) {
      const children = walk(node.children);
      const selfMatches = normalizeQueryText(`${node.number} ${node.title}`).includes(normalizedQuery);
      if (selfMatches || children.length > 0) out.push({ ...node, children });
    }
    return out;
  };

  return walk(nodes);
};

export const sectionSiblingOrderAfterMove = <Row extends SectionOrderRow>({
  sections,
  sectionId,
  direction,
}: SectionSiblingMoveInput<Row>): SectionSiblingMoveResult<Row["_id"]> => {
  const current = sections.find((section) => section._id === sectionId);
  if (current === undefined) return { kind: "unchanged", reason: "sectionNotFound" };

  const siblings = sections
    .filter((section) => section.parentId === current.parentId)
    .sort((a, b) => a.order - b.order);
  const index = siblings.findIndex((section) => section._id === sectionId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= siblings.length) {
    return { kind: "unchanged", reason: "atBoundary" };
  }

  const orderedIds = siblings.map((section) => section._id);
  const currentId = orderedIds[index];
  const nextId = orderedIds[nextIndex];
  if (currentId === undefined || nextId === undefined) return { kind: "unchanged", reason: "atBoundary" };

  orderedIds[index] = nextId;
  orderedIds[nextIndex] = currentId;
  return { kind: "changed", orderedIds };
};

/** Drop hidden (repealed) nodes and any of their descendants — the client view. */
export const visibleTree = <SectionId extends string>(
  nodes: readonly SectionTreeNode<SectionId>[],
): SectionTreeNode<SectionId>[] =>
  nodes
    .filter((n) => !n.hidden)
    .map((n) => ({ ...n, children: visibleTree(n.children) }));

/** Legislation references are uppercase alphanumeric business keys, e.g. ELRWA1991. */
export const isValidReference = (ref: string): boolean => /^[A-Z0-9]{3,}$/.test(ref);

/**
 * The title an old piece of legislation gets once it's been repealed and
 * replaced, matching the convention in the procedures, e.g.
 * "Privacy Regulation 2013 (Cth) REPEALED AND REPLACED BY Privacy Regulations
 *  2025 WITH EFFECT 1 APRIL 2026".
 */
export const repealedTitle = (
  originalTitle: string,
  replacedByTitle: string,
  effectiveDate: Date,
): string => {
  const when = formatAuDate(effectiveDate.getTime()).toUpperCase();
  return `${originalTitle} REPEALED AND REPLACED BY ${replacedByTitle} WITH EFFECT ${when}`;
};

/** Prefix used in the Excel upload template's "Legislation" column. */
export const sectionPrefix = (kind: LegislationKind): string =>
  kind === "act" ? "Section" : "Regulation";

export type LegislationSummary = {
  readonly title: string;
  readonly reference: string;
  readonly jurisdiction: Jurisdiction;
};

export type LegislationBrowserTypeFilter =
  | { readonly kind: "all" }
  | { readonly kind: "legislationKind"; readonly legislationKind: LegislationKind };

export type LegislationBrowserRow<LegislationId extends string = string> = LegislationSummary & {
  readonly _id: LegislationId;
  readonly kind: LegislationKind;
  readonly status: LegislationStatus;
};

export type LegislationBrowserRowView = {
  readonly repealed: boolean;
  readonly titleToneClass: string;
  readonly statusBadgeKind: "repealed" | null;
};

export type LegislationBrowserSectionSearchView<Row> =
  | { readonly kind: "hidden" }
  | {
      readonly kind: "loading";
      readonly query: string;
      readonly countLabel: "Searching...";
    }
  | {
      readonly kind: "empty";
      readonly query: string;
      readonly countLabel: string;
      readonly message: string;
    }
  | {
      readonly kind: "ready";
      readonly query: string;
      readonly countLabel: string;
      readonly matches: readonly Row[];
    };

export type LegislationBrowserRowsInput<LegislationId extends string, Row extends LegislationBrowserRow<LegislationId>> = {
  readonly legislation: readonly Row[];
  readonly subscribedIds: ReadonlySet<LegislationId>;
  readonly type: LegislationBrowserTypeFilter;
  readonly subscribedOnly: boolean;
  readonly query: string;
};

export const legislationBrowserTypeFilterFromInput = (value: string): LegislationBrowserTypeFilter => {
  if (value === "ALL") return { kind: "all" };
  const parsed = legislationKindInputResult(value);
  return parsed.ok ? { kind: "legislationKind", legislationKind: parsed.value } : { kind: "all" };
};

export const legislationBrowserTypeFilterToInput = (filter: LegislationBrowserTypeFilter): "ALL" | LegislationKind => {
  switch (filter.kind) {
    case "all":
      return "ALL";
    case "legislationKind":
      return filter.legislationKind;
  }
};

export const legislationBrowserRowView = (
  row: Pick<LegislationBrowserRow, "status">,
): LegislationBrowserRowView =>
  row.status === "inactive"
    ? {
        repealed: true,
        titleToneClass: "text-slate-400 line-through",
        statusBadgeKind: "repealed",
      }
    : {
        repealed: false,
        titleToneClass: "text-slate-800",
        statusBadgeKind: null,
      };

export const sectionSearchMatchCount = (matches: readonly unknown[] | undefined): number | null =>
  matches === undefined ? null : matches.length;

export const legislationBrowserSectionSearchView = <Row>({
  query,
  matches,
}: {
  readonly query: string;
  readonly matches: readonly Row[] | undefined;
}): LegislationBrowserSectionSearchView<Row> => {
  const normalizedQuery = query.trim();
  if (normalizedQuery.length < 2) return { kind: "hidden" };
  if (matches === undefined) {
    return { kind: "loading", query: normalizedQuery, countLabel: "Searching..." };
  }
  if (matches.length === 0) {
    return {
      kind: "empty",
      query: normalizedQuery,
      countLabel: formatCountLabel(0, "result"),
      message: `No section text matches "${normalizedQuery}".`,
    };
  }
  return {
    kind: "ready",
    query: normalizedQuery,
    countLabel: formatCountLabel(matches.length, "result"),
    matches,
  };
};

export type ObligationSearchMatch<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislation: {
    readonly _id: LegislationId;
    readonly title: string;
    readonly reference: string;
    readonly jurisdiction: Jurisdiction;
  };
  readonly section: {
    readonly _id: SectionId;
    readonly number: string;
    readonly title: string;
  };
  readonly snippet: string;
};

export type ObligationExportRow = {
  readonly obligation: string;
  readonly source: string;
  readonly section: string;
  readonly jurisdiction: Jurisdiction;
  readonly topic: string;
  readonly responsiblePerson: string;
  readonly status: "Needs review" | "Covered" | "Partial" | "Missing" | "Unclear" | "Needs legal review";
  readonly evidenceControlReference: string;
  readonly notes: string;
  readonly sourceUrl: string;
};

export const obligationExportRow = <LegislationId extends string, SectionId extends string>({
  match,
  topic,
  sourceUrl,
  responsiblePerson = "",
  status = "Needs review",
  evidenceControlReference = "",
  notes = "",
}: {
  readonly match: ObligationSearchMatch<LegislationId, SectionId>;
  readonly topic: string;
  readonly sourceUrl: string;
  readonly responsiblePerson?: string;
  readonly status?: ObligationExportRow["status"];
  readonly evidenceControlReference?: string;
  readonly notes?: string;
}): ObligationExportRow => ({
  obligation: match.snippet,
  source: match.legislation.title + " (" + match.legislation.reference + ")",
  section: [match.section.number, match.section.title].filter((part) => part.trim() !== "").join(" "),
  jurisdiction: match.legislation.jurisdiction,
  topic: topic.trim(),
  responsiblePerson: responsiblePerson.trim(),
  status,
  evidenceControlReference: evidenceControlReference.trim(),
  notes: notes.trim(),
  sourceUrl,
});

const OBLIGATION_EXPORT_HEADERS = [
  "obligation",
  "source",
  "section",
  "jurisdiction",
  "topic",
  "responsible person",
  "status",
  "evidence/control reference",
  "notes",
  "source URL",
] as const;

const csvCell = (value: string): string => {
  const normalized = value.replace(/\r?\n/g, " ");
  return /[",\n]/.test(normalized) ? "\"" + normalized.replace(/"/g, "\"\"") + "\"" : normalized;
};

const obligationExportRowCells = (row: ObligationExportRow): readonly string[] => [
  row.obligation,
  row.source,
  row.section,
  row.jurisdiction,
  row.topic,
  row.responsiblePerson,
  row.status,
  row.evidenceControlReference,
  row.notes,
  row.sourceUrl,
];

export const obligationExportCsv = (rows: readonly ObligationExportRow[]): string => [
  OBLIGATION_EXPORT_HEADERS.map(csvCell).join(","),
  ...rows.map((row) => obligationExportRowCells(row).map(csvCell).join(",")),
].join("\n");

export const obligationExportFilename = (query: string): string => {
  const slug = query.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return (slug === "" ? "obligations" : "obligations-" + slug) + ".csv";
};

const legislationMatchesBrowserType = (
  legislation: Pick<LegislationBrowserRow, "kind">,
  filter: LegislationBrowserTypeFilter,
): boolean => {
  switch (filter.kind) {
    case "all":
      return true;
    case "legislationKind":
      return legislation.kind === filter.legislationKind;
  }
};

export const legislationBrowserRows = <LegislationId extends string, Row extends LegislationBrowserRow<LegislationId>>({
  legislation,
  subscribedIds,
  type,
  subscribedOnly,
  query,
}: LegislationBrowserRowsInput<LegislationId, Row>): Row[] =>
  legislation
    .filter((row) => legislationMatchesBrowserType(row, type))
    .filter((row) => !subscribedOnly || subscribedIds.has(row._id))
    .filter((row) => matchesQuery(row, query));

/** Case-insensitive title/reference filter for the legislation picker. */
export const matchesQuery = (leg: LegislationSummary, query: string): boolean => {
  const normalizedQuery = normalizeQueryText(query);
  if (normalizedQuery === "") return true;
  return (
    normalizeQueryText(leg.title).includes(normalizedQuery) ||
    normalizeQueryText(leg.reference).includes(normalizedQuery)
  );
};
